import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Leaf, Recycle, GraduationCap, Users, TrendingUp, Globe, Globe2, CheckCircle2, MapPin, RefreshCw, BookOpen, ArrowRight } from 'lucide-react';
import { PhoneImage } from '../components/site/PhoneImage';
import { useT } from '../components/site/i18n';
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 30
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-50px'
}} transition={{
  duration: 0.7,
  delay,
  ease: [0.21, 0.47, 0.32, 0.98]
}} className={className} data-id="element-588" data-mp="1o6mwkkxs096f">
    {children}
  </motion.div>;
export function ODS() {
  const t = useT();
  return <div className="min-h-screen bg-brand-cream dark:bg-gray-900 pt-24 relative overflow-hidden transition-colors duration-300" data-id="element-589" data-mp="26xe0h6yzv7df">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05] pointer-events-none" style={{
      backgroundImage: 'radial-gradient(currentColor 1px, transparent 1px)',
      backgroundSize: '24px 24px'
    }} data-id="element-590" data-mp="3gxc0ytkaplag"></div>

      {/* Hero Section */}
      <section className="px-6 py-16 md:py-24 max-w-7xl mx-auto text-center relative z-10" data-id="element-591" data-mp="3pvnq0r5ikb0s">
        <FadeIn data-id="element-592" data-mp="3uv0jhuqw4gcc">
          <div className="w-16 h-16 bg-brand-yellow/20 dark:bg-brand-yellow/10 rounded-2xl flex items-center justify-center mx-auto mb-8 text-brand-yellow-dark dark:text-brand-yellow shadow-sm border border-brand-yellow/30 dark:border-brand-yellow/20 transform rotate-3" data-id="element-593" data-mp="3qtskyadhatq">
            <Globe2 size={32} data-id="element-594" data-mp="cl86awf98daq" />
          </div>
          <span className="text-brand-green-dark dark:text-brand-green-light font-bold tracking-wider uppercase text-sm mb-6 block" data-id="element-595" data-mp="hiqfmnx6ouj9">
            {t.ods.heroEyebrow}
          </span>
          <h1 className="text-5xl md:text-7xl font-sans font-extrabold text-brand-charcoal dark:text-white mb-8 leading-[1.1] tracking-tight max-w-4xl mx-auto" data-id="element-596" data-mp="2ik6hogiexpck">
            {t.ods.heroTitle1}{' '}
            <span className="relative inline-block" data-id="element-597" data-mp="2s9zhkk970g0z">
              <span className="relative z-10 font-display text-brand-green dark:text-brand-green-light font-normal text-6xl md:text-8xl tracking-normal transform -rotate-2 inline-block" data-id="element-598" data-mp="2sbu2vzodma5l">
                {t.ods.heroTitle2}
              </span>
              {/* SVG Highlight */}
              <svg className="absolute -bottom-2 left-0 w-full h-4 text-brand-yellow -z-10" viewBox="0 0 100 10" preserveAspectRatio="none" data-id="element-599" data-mp="2x8atd0j1t0lu">
                <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="4" fill="none" strokeLinecap="round" aria-hidden="true" data-id="element-600" data-mp="36bbyaxw6glnx" />
              </svg>
            </span>
          </h1>
          <p className="text-xl text-brand-charcoal/70 dark:text-gray-300 font-sans leading-relaxed max-w-3xl mx-auto" data-id="element-601" data-mp="3njuym3nsjwzi">
            {t.ods.heroDesc}
          </p>
        </FadeIn>
      </section>

      {/* Metrics */}
      <section className="bg-brand-green-dark text-white py-20 px-6 relative overflow-hidden" data-id="element-602" data-mp="3drgtr21f2a94">
        {/* Organic shape background */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-brand-green/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none" aria-hidden="true" data-id="element-603" data-mp="34sbyc4l5ix16"></div>

        <div className="max-w-7xl mx-auto relative z-10" data-id="element-604" data-mp="2uv7c64rb31kd">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 text-center" data-id="element-605" data-mp="2uws0vdqxvqab">
            <FadeIn delay={0.1} data-id="element-606" data-mp="2zu0kzjvne4e7">
              <div className="text-5xl md:text-6xl font-display text-brand-yellow mb-3 tracking-normal transform -rotate-2" data-id="element-607" data-mp="t3kfbmkqgcfz">
                0
              </div>
              <div className="text-white/80 text-sm font-bold tracking-wide uppercase leading-relaxed" data-id="element-608" data-mp="y1m5ergktc0y">
                {t.ods.stat1Desc}
              </div>
            </FadeIn>
            <FadeIn delay={0.2} data-id="element-609" data-mp="2q59qzckckt5a">
              <div className="text-5xl md:text-6xl font-display text-brand-yellow mb-3 tracking-normal transform rotate-1" data-id="element-610" data-mp="2q24320ngfxb5">
                R$ 0
              </div>
              <div className="text-white/80 text-sm font-bold tracking-wide uppercase leading-relaxed" data-id="element-611" data-mp="2g4zghzpp5ztt">
                {t.ods.stat2Desc}
              </div>
            </FadeIn>
            <FadeIn delay={0.3} data-id="element-612" data-mp="26fpik3869mxs">
              <div className="text-5xl md:text-6xl font-display text-brand-yellow mb-3 tracking-normal transform -rotate-1" data-id="element-613" data-mp="26eo8o22w76hn">
                0
              </div>
              <div className="text-white/80 text-sm font-bold tracking-wide uppercase leading-relaxed" data-id="element-614" data-mp="1xg2knreniz9h">
                {t.ods.stat3Desc}
              </div>
            </FadeIn>
            <FadeIn delay={0.4} data-id="element-615" data-mp="1nhdc4p8nar5w">
              <div className="text-5xl md:text-6xl font-display text-brand-yellow mb-3 tracking-normal transform rotate-2" data-id="element-616" data-mp="1niemsslr1bn3">
                0
              </div>
              <div className="text-white/80 text-sm font-bold tracking-wide uppercase leading-relaxed" data-id="element-617" data-mp="1ibzq0k5p3tur">
                {t.ods.stat4Desc}
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Impact in Practice */}
      <section className="py-24 px-6 max-w-7xl mx-auto relative z-10" data-id="element-618" data-mp="1s9xg7d7480q0">
        <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-619" data-mp="unqafzz2s2c2">
          <FadeIn className="order-2 lg:order-1 relative flex justify-center lg:justify-start" data-id="element-620" data-mp="zlicvnxcj6ui">
            <div className="absolute inset-0 bg-brand-yellow/10 dark:bg-brand-yellow/5 rounded-full blur-3xl -z-10 transform scale-125" data-id="element-621" data-mp="14kuvfwh6prra"></div>
            <PhoneImage src="https://cdn.magicpatterns.com/uploads/rBKDBrCYsC8iZEEEAuogN1/Impact.png" alt="Módulo BairrouImpacta! mostrando missões de sustentabilidade" className="transform -rotate-2 hover:rotate-0 transition-transform duration-500" data-id="element-622" data-mp="19cd0avmbwchj" />
          </FadeIn>
          <FadeIn className="order-1 lg:order-2" data-id="element-623" data-mp="8srpjzt802je">
            <span className="text-brand-green-dark dark:text-brand-green-light font-bold tracking-wider uppercase text-sm mb-4 block" data-id="element-624" data-mp="3w1570aduks6">
              {t.ods.impactEyebrow}
            </span>
            <h2 className="text-4xl md:text-5xl font-sans font-extrabold text-brand-charcoal dark:text-white mb-6 tracking-tight" data-id="element-625" data-mp="dlkog720cgnk">
              {t.ods.impactTitle}
            </h2>
            <p className="text-lg text-brand-charcoal/70 dark:text-gray-300 mb-10 leading-relaxed" data-id="element-626" data-mp="3h21bhiaypmt2">
              {t.ods.impactDesc}
            </p>
            <ul className="space-y-6 relative" data-id="element-627" data-mp="3w023404t5che">
              {/* Dotted connector (desktop only) */}
              <div className="hidden sm:block absolute top-4 bottom-4 left-4 w-0.5 border-l-2 border-dashed border-brand-green/20 dark:border-brand-green/10 z-0" aria-hidden="true" data-id="element-628" data-mp="3qzeflofsfdrx"></div>

              {[t.ods.impactList1, t.ods.impactList2, t.ods.impactList3, t.ods.impactList4].map((item, i) => <li key={i} className="flex items-center gap-4 relative z-10" data-id="element-629" data-mp="1iqhn818jxxen" data-mpi={i}>
                  <div className="w-8 h-8 rounded-full bg-white dark:bg-gray-800 border-2 border-brand-yellow text-brand-yellow-dark dark:text-brand-yellow flex items-center justify-center shrink-0 shadow-sm" data-id="element-630" data-mp="14udxak4pob7z">
                    <CheckCircle2 size={16} data-id="element-631" data-mp="1wkqz8nzjgrqq" />
                  </div>
                  <span className="text-brand-charcoal dark:text-gray-200 font-medium" data-id="element-632" data-mp="21i0r0aaehfhq">
                    {item}
                  </span>
                </li>)}
            </ul>
          </FadeIn>
        </div>
      </section>

      {/* SDGs Grid */}
      <section className="bg-white dark:bg-gray-800 py-24 px-6 relative z-10 transition-colors duration-300" data-id="element-633" data-mp="2fj3dgo5b4rt6">
        <div className="max-w-7xl mx-auto" data-id="element-634" data-mp="2ka3adteff9vo">
          <FadeIn className="text-center mb-20" data-id="element-635" data-mp="2kautw5ur1h5l">
            <span className="text-brand-green-dark dark:text-brand-green-light font-bold tracking-wider uppercase text-sm mb-4 block" data-id="element-636" data-mp="1vjfgec58an6p">
              {t.ods.odsEyebrow}
            </span>
            <h2 className="text-4xl md:text-5xl font-sans font-extrabold text-brand-charcoal dark:text-white mb-6 tracking-tight" data-id="element-637" data-mp="1lsuk4vd11uqy">
              {t.ods.odsTitle}
            </h2>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-638" data-mp="2ehrso6rfsd6i">
            {[{
            icon: <TrendingUp size={28} data-id="element-639" data-mp="29ioth2y5116g" />,
            title: t.ods.ods1Title,
            desc: t.ods.ods1Desc,
            color: 'bg-red-500',
            number: '8'
          }, {
            icon: <MapPin size={28} data-id="element-640" data-mp="1zlaez8vrymgs" />,
            title: t.ods.ods2Title,
            desc: t.ods.ods2Desc,
            color: 'bg-orange-500',
            number: '11'
          }, {
            icon: <RefreshCw size={28} data-id="element-641" data-mp="o4kiihh5l90d" />,
            title: t.ods.ods3Title,
            desc: t.ods.ods3Desc,
            color: 'bg-yellow-600',
            number: '12'
          }, {
            icon: <BookOpen size={28} data-id="element-642" data-mp="34ppio9re7v1j" />,
            title: t.ods.ods4Title,
            desc: t.ods.ods4Desc,
            color: 'bg-red-600',
            number: '4'
          }].map((ods, i) => <FadeIn key={i} delay={i * 0.1} className="flex flex-col sm:flex-row gap-6 bg-brand-cream/50 dark:bg-gray-900/50 p-8 rounded-3xl border-l-4 border-l-brand-yellow border-y border-r border-gray-100 dark:border-gray-700 hover:bg-white dark:hover:bg-gray-900 hover:shadow-md transition-all duration-300 relative overflow-hidden group" data-id="element-643" data-mp="3jgyc6tyf20jy" data-mpi={i}>
                <div className="absolute top-0 right-0 w-16 h-16 bg-brand-yellow/10 dark:bg-brand-yellow/5 rounded-bl-full -z-10 group-hover:scale-150 transition-transform duration-500" data-id="element-644" data-mp="1y3v800rf08ps"></div>
                <div className={`w-16 h-16 ${ods.color} text-white rounded-2xl flex items-center justify-center shrink-0 shadow-sm transform -rotate-3 group-hover:rotate-0 transition-transform`} data-id="element-645" data-mp="esd3b58b7jce">
                  <span className="font-sans font-extrabold text-2xl tracking-tight" data-id="element-646" data-mp="ophq979zbiu9">
                    {ods.number}
                  </span>
                </div>
                <div data-id="element-647" data-mp="xr8x4fg99vpp">
                  <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mb-3" data-id="element-648" data-mp="xpxrea392dvy">
                    {ods.title}
                  </h3>
                  <p className="text-brand-charcoal/60 dark:text-gray-300 leading-relaxed" data-id="element-649" data-mp="12npgkwciflwg">
                    {ods.desc}
                  </p>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>
    </div>;
}